import React, { useState, useEffect } from 'react';

const Attendance = ({ userRole, userId }) => {
  const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth());
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [selectedDate, setSelectedDate] = useState(null);
  const [showCheckModal, setShowCheckModal] = useState(false);
  const [checkInTime, setCheckInTime] = useState('');
  const [checkOutTime, setCheckOutTime] = useState('');
  const [attendanceData, setAttendanceData] = useState({});
  const [currentUser, setCurrentUser] = useState(null);

  // Get current user from localStorage
  useEffect(() => {
    const user = JSON.parse(localStorage.getItem('user') || '{}');
    setCurrentUser(user);
    loadAttendanceData();
  }, []);

  // Load attendance data from localStorage or initialize
  const loadAttendanceData = () => {
    const savedData = localStorage.getItem('attendanceData');
    if (savedData) {
      setAttendanceData(JSON.parse(savedData));
    } else {
      const initialData = {};
      const employees = getEmployeesList();
      employees.forEach(emp => {
        initialData[emp.id] = {};
      });
      setAttendanceData(initialData);
    }
  };

  // Save attendance data to localStorage
  const saveAttendanceData = (data) => {
    localStorage.setItem('attendanceData', JSON.stringify(data));
    setAttendanceData(data);
  };

  // Get employees list based on role
  const getEmployeesList = () => {
    const allEmployees = [
      { id: 1, name: 'Harsh Sharma', role: 'employee', department: 'Software Development', email: 'harsh@aizops.com' },
      { id: 2, name: 'Aman Verma', role: 'manager', department: 'IT', email: 'aman@aizops.com' },
      { id: 3, name: 'Saurav Kumar', role: 'employee', department: 'Marketing', email: 'saurav@aizops.com' },
      { id: 4, name: 'Sanjiv Singh', role: 'hr', department: 'HR', email: 'sanjiv@aizops.com' },
      { id: 5, name: 'Dr. Apj Abdul Kalam', role: 'employee', department: 'R&D', email: 'kalam@aizops.com' },
      { id: 6, name: 'John Doe', role: 'employee', department: 'IT', email: 'john@aizops.com' },
      { id: 7, name: 'Jane Smith', role: 'hr', department: 'HR', email: 'jane@aizops.com' },
      { id: 8, name: 'Mike Johnson', role: 'admin', department: 'Administration', email: 'mike@aizops.com' },
    ];

    if (['hr', 'admin', 'superadmin'].includes(userRole)) {
      return allEmployees;
    } else if (userRole === 'manager') {
      return allEmployees.filter(emp => emp.role === 'employee' || emp.id === currentUser?.id);
    } else {
      return allEmployees.filter(emp => emp.id === currentUser?.id);
    }
  };

  const employees = getEmployeesList();

  const getDaysInMonth = (year, month) => {
    return new Date(year, month + 1, 0).getDate();
  };

  const getFirstDayOfMonth = (year, month) => {
    return new Date(year, month, 1).getDay();
  };

  const isToday = (year, month, day) => {
    const today = new Date();
    return today.getFullYear() === year && today.getMonth() === month && today.getDate() === day;
  };

  const isPastDate = (year, month, day) => {
    const date = new Date(year, month, day);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return date < today;
  };

  // Check if a date is a weekend (Saturday or Sunday)
  const isWeekend = (year, month, day) => {
    const date = new Date(year, month, day);
    const dayOfWeek = date.getDay();
    return dayOfWeek === 0 || dayOfWeek === 6; // Sunday or Saturday
  };

  // Check if a date is a holiday (for demo, adding some fixed holidays)
  const isHoliday = (year, month, day) => {
    // Example holidays: Jan 26 (Republic Day), Aug 15 (Independence Day), Oct 2 (Gandhi Jayanti)
    const holidays = [
      { month: 0, day: 26 }, // Jan 26
      { month: 7, day: 15 }, // Aug 15
      { month: 9, day: 2 },  // Oct 2
    ];
    return holidays.some(h => h.month === month && h.day === day);
  };

  // Function to check if a day has leave applied (example logic - can be extended)
  const hasLeave = (employeeId, date) => {
    // For demo, applying leave on some random dates
    // In real app, this would come from a leave management system
    const dateKey = `${date.getFullYear()}-${date.getMonth() + 1}-${date.getDate()}`;
    const leaveData = JSON.parse(localStorage.getItem('leaveData') || '{}');
    return leaveData[employeeId]?.[dateKey] === 'leave';
  };

  // Function to check if a day has comp-off
  const hasCompOff = (employeeId, date) => {
    // For demo, applying comp-off on some specific dates
    // In real app, this would come from a comp-off system
    const dateKey = `${date.getFullYear()}-${date.getMonth() + 1}-${date.getDate()}`;
    const compOffData = JSON.parse(localStorage.getItem('compOffData') || '{}');
    return compOffData[employeeId]?.[dateKey] === 'comp-off';
  };

  const markAttendanceForDate = (employeeId, date, checkIn, checkOut, status) => {
    const dateKey = `${date.getFullYear()}-${date.getMonth() + 1}-${date.getDate()}`;
    const newData = { ...attendanceData };
    
    if (!newData[employeeId]) {
      newData[employeeId] = {};
    }
    
    newData[employeeId][dateKey] = {
      checkIn: checkIn || '09:00 AM',
      checkOut: checkOut || '06:00 PM',
      status: status || 'present',
      timestamp: new Date().toISOString()
    };
    
    saveAttendanceData(newData);
    alert(`Attendance marked for ${getEmployeeName(employeeId)} on ${dateKey}`);
  };

  const getAttendanceForDate = (employeeId, date) => {
    const dateKey = `${date.getFullYear()}-${date.getMonth() + 1}-${date.getDate()}`;
    return attendanceData[employeeId]?.[dateKey] || null;
  };

  const getEmployeeName = (id) => {
    const emp = employees.find(e => e.id === id);
    return emp ? emp.name : 'Unknown';
  };

  const handleCheckInOut = () => {
    if (!selectedDate) return;
    
    const now = new Date();
    const currentTime = now.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' });
    
    if (!checkInTime) {
      setCheckInTime(currentTime);
      alert(`Checked in at ${currentTime}`);
    } else if (!checkOutTime) {
      setCheckOutTime(currentTime);
      alert(`Checked out at ${currentTime}`);
      
      markAttendanceForDate(currentUser?.id, selectedDate, checkInTime, currentTime, 'present');
      setCheckInTime('');
      setCheckOutTime('');
      setShowCheckModal(false);
      setSelectedDate(null);
    }
  };

  const renderEmployeeCalendar = (employee) => {
    const daysInMonth = getDaysInMonth(selectedYear, selectedMonth);
    const firstDay = getFirstDayOfMonth(selectedYear, selectedMonth);
    const days = [];
    
    for (let i = 0; i < firstDay; i++) {
      days.push(<div key={`empty-${i}`} className="h-28 bg-gray-50 border border-gray-100"></div>);
    }
    
    for (let day = 1; day <= daysInMonth; day++) {
      const date = new Date(selectedYear, selectedMonth, day);
      const attendance = getAttendanceForDate(employee.id, date);
      const isDateToday = isToday(selectedYear, selectedMonth, day);
      const isPast = isPastDate(selectedYear, selectedMonth, day);
      const weekend = isWeekend(selectedYear, selectedMonth, day);
      const holiday = isHoliday(selectedYear, selectedMonth, day);
      const leave = hasLeave(employee.id, date);
      const compOff = hasCompOff(employee.id, date);
      
      // Determine status and color
      let statusColor = '';
      let statusText = '';
      let statusLabel = '';
      
      // Priority: comp-off > leave > holiday > weekend > attendance > absent
      if (compOff) {
        statusColor = 'bg-purple-600';
        statusText = 'Comp Off';
        statusLabel = 'Comp Off';
      } else if (leave) {
        statusColor = 'bg-blue-500';
        statusText = 'Leave';
        statusLabel = 'Leave';
      } else if (holiday) {
        statusColor = 'bg-gray-400';
        statusText = 'Holiday';
        statusLabel = 'Holiday';
      } else if (weekend) {
        statusColor = 'bg-gray-400';
        statusText = 'Holiday';
        statusLabel = 'Weekend';
      } else if (attendance) {
        if (attendance.status === 'present') {
          statusColor = 'bg-green-500';
          statusText = 'Present';
          statusLabel = 'Present';
        } else if (attendance.status === 'absent') {
          statusColor = 'bg-red-500';
          statusText = 'Absent';
          statusLabel = 'Absent';
        } else if (attendance.status === 'half-day') {
          statusColor = 'bg-yellow-500';
          statusText = 'Half Day';
          statusLabel = 'Half Day';
        }
      } else if (isPast && !weekend && !holiday) {
        statusColor = 'bg-red-500';
        statusText = 'Absent';
        statusLabel = 'Absent';
      }
      
      days.push(
        <div 
          key={day} 
          className={`h-28 border p-2 relative ${statusColor ? 'text-white' : 'bg-white'} ${isDateToday ? 'ring-2 ring-indigo-500' : ''} ${statusColor === 'bg-green-500' ? 'bg-green-500' : statusColor === 'bg-red-500' ? 'bg-red-500' : statusColor === 'bg-yellow-500' ? 'bg-yellow-500' : statusColor === 'bg-blue-500' ? 'bg-blue-500' : statusColor === 'bg-purple-600' ? 'bg-purple-600' : statusColor === 'bg-gray-400' ? 'bg-gray-400' : 'bg-white'}`}
        >
          <span className={`text-sm font-medium ${statusColor ? 'text-white' : isDateToday ? 'text-indigo-600' : 'text-gray-700'}`}>{day}</span>
          
          {statusLabel && (
            <div className="mt-1 text-xs font-semibold">
              {statusLabel}
            </div>
          )}
          
          {attendance && attendance.checkIn && attendance.checkOut && (
            <div className="mt-1 text-xs opacity-90">
              <p>IN: {attendance.checkIn}</p>
              <p>OUT: {attendance.checkOut}</p>
            </div>
          )}
          
          {isDateToday && !attendance && !weekend && !holiday && userRole === 'employee' && employee.id === currentUser?.id && (
            <button
              onClick={() => {
                setSelectedDate(date);
                setShowCheckModal(true);
              }}
              className="absolute bottom-2 right-2 text-xs bg-white text-indigo-600 px-2 py-1 rounded shadow hover:bg-indigo-50 font-medium"
            >
              Check In
            </button>
          )}
        </div>
      );
    }
    
    return days;
  };

  const getMonthlySummary = () => {
    const summary = {};
    employees.forEach(emp => {
      let present = 0;
      let absent = 0;
      let halfDay = 0;
      let leave = 0;
      let compOff = 0;
      let holiday = 0;
      const daysInMonth = getDaysInMonth(selectedYear, selectedMonth);
      
      for (let day = 1; day <= daysInMonth; day++) {
        const date = new Date(selectedYear, selectedMonth, day);
        const attendance = getAttendanceForDate(emp.id, date);
        const weekend = isWeekend(selectedYear, selectedMonth, day);
        const holidayDate = isHoliday(selectedYear, selectedMonth, day);
        const leaveApplied = hasLeave(emp.id, date);
        const compOffApplied = hasCompOff(emp.id, date);
        
        if (compOffApplied) {
          compOff++;
        } else if (leaveApplied) {
          leave++;
        } else if (holidayDate || weekend) {
          holiday++;
        } else if (attendance) {
          if (attendance.status === 'present') present++;
          else if (attendance.status === 'absent') absent++;
          else if (attendance.status === 'half-day') halfDay++;
        } else {
          if (isPastDate(selectedYear, selectedMonth, day) && !weekend && !holidayDate) {
            absent++;
          }
        }
      }
      
      summary[emp.id] = { present, absent, halfDay, leave, compOff, holiday, total: daysInMonth };
    });
    return summary;
  };

  const monthlySummary = getMonthlySummary();
  const showHRView = ['hr', 'admin', 'superadmin'].includes(userRole);
  const showManagerView = userRole === 'manager';
  const months = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'
  ];
  
  // Generate years from 2020 to 2099
  const generateYears = () => {
    const currentYear = new Date().getFullYear();
    const years = [];
    for (let year = 2020; year <= 2099; year++) {
      years.push(year);
    }
    return years;
  };
  
  const years = generateYears();

  // Handle year selection with proper validation
  const handleYearChange = (e) => {
    const newYear = parseInt(e.target.value);
    setSelectedYear(newYear);
  };

  // Handle month change
  const handleMonthChange = (e) => {
    setSelectedMonth(parseInt(e.target.value));
  };

  return (
    <div>
      <div className="flex justify-between items-center mb-6 flex-wrap gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-800">Attendance Management</h1>
          <p className="text-sm text-gray-500 mt-1">Track and manage employee attendance</p>
        </div>
        
        <div className="flex gap-3">
          <select
            className="px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
            value={selectedMonth}
            onChange={handleMonthChange}
          >
            {months.map((month, index) => (
              <option key={index} value={index}>{month}</option>
            ))}
          </select>
          
          <select
            className="px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
            value={selectedYear}
            onChange={handleYearChange}
          >
            {years.map(year => (
              <option key={year} value={year}>{year}</option>
            ))}
          </select>
        </div>
      </div>

      {(showHRView || showManagerView) && (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
          <div className="bg-white rounded-xl border border-gray-200 p-4">
            <p className="text-sm text-gray-500">Total Employees</p>
            <p className="text-2xl font-bold text-gray-800">{employees.length}</p>
          </div>
          <div className="bg-white rounded-xl border border-gray-200 p-4">
            <p className="text-sm text-gray-500">Present Today</p>
            <p className="text-2xl font-bold text-green-600">
              {employees.filter(emp => {
                const attendance = getAttendanceForDate(emp.id, new Date());
                return attendance?.status === 'present';
              }).length}
            </p>
          </div>
          <div className="bg-white rounded-xl border border-gray-200 p-4">
            <p className="text-sm text-gray-500">Absent Today</p>
            <p className="text-2xl font-bold text-red-600">
              {employees.filter(emp => {
                const attendance = getAttendanceForDate(emp.id, new Date());
                return !attendance || attendance.status === 'absent';
              }).length}
            </p>
          </div>
          <div className="bg-white rounded-xl border border-gray-200 p-4">
            <p className="text-sm text-gray-500">Attendance Rate</p>
            <p className="text-2xl font-bold text-indigo-600">
              {Math.round((employees.filter(emp => {
                const attendance = getAttendanceForDate(emp.id, new Date());
                return attendance?.status === 'present';
              }).length / employees.length) * 100)}%
            </p>
          </div>
        </div>
      )}

      {/* Legend */}
      <div className="flex flex-wrap gap-4 mb-6 p-4 bg-gray-50 rounded-lg">
        <div className="flex items-center gap-2">
          <div className="w-6 h-6 bg-green-500 rounded"></div>
          <span className="text-sm">Present</span>
        </div>
        <div className="flex items-center gap-2">
          <div className="w-6 h-6 bg-red-500 rounded"></div>
          <span className="text-sm">Absent</span>
        </div>
        <div className="flex items-center gap-2">
          <div className="w-6 h-6 bg-yellow-500 rounded"></div>
          <span className="text-sm">Half Day</span>
        </div>
        <div className="flex items-center gap-2">
          <div className="w-6 h-6 bg-gray-400 rounded"></div>
          <span className="text-sm">Holiday/Weekend</span>
        </div>
        <div className="flex items-center gap-2">
          <div className="w-6 h-6 bg-blue-500 rounded"></div>
          <span className="text-sm">Leave</span>
        </div>
        <div className="flex items-center gap-2">
          <div className="w-6 h-6 bg-purple-600 rounded"></div>
          <span className="text-sm">Comp Off</span>
        </div>
      </div>

      {(showHRView || showManagerView) && (
        <div className="bg-white rounded-xl shadow-sm overflow-hidden mb-6">
          <div className="px-6 py-4 border-b border-gray-200">
            <h2 className="text-lg font-semibold text-gray-800">Monthly Summary - {months[selectedMonth]} {selectedYear}</h2>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Employee</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Department</th>
                  <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase">Present</th>
                  <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase">Absent</th>
                  <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase">Half Day</th>
                  <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase">Leave</th>
                  <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase">Comp Off</th>
                  <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase">Holiday</th>
                  <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase">Total Days</th>
                  <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase">Attendance %</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {employees.map(emp => {
                  const summary = monthlySummary[emp.id];
                  const workingDays = summary.total - summary.holiday;
                  const percentage = workingDays > 0 ? Math.round(((summary.present + summary.halfDay * 0.5) / workingDays) * 100) : 0;
                  return (
                    <tr key={emp.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4">
                        <div>
                          <p className="text-sm font-medium text-gray-900">{emp.name}</p>
                          <p className="text-xs text-gray-500 capitalize">{emp.role}</p>
                        </div>
                       </td>
                      <td className="px-6 py-4 text-sm text-gray-600">{emp.department}</td>
                      <td className="px-6 py-4 text-center text-sm font-medium text-green-600">{summary.present}</td>
                      <td className="px-6 py-4 text-center text-sm font-medium text-red-600">{summary.absent}</td>
                      <td className="px-6 py-4 text-center text-sm font-medium text-yellow-600">{summary.halfDay}</td>
                      <td className="px-6 py-4 text-center text-sm font-medium text-blue-600">{summary.leave}</td>
                      <td className="px-6 py-4 text-center text-sm font-medium text-purple-600">{summary.compOff}</td>
                      <td className="px-6 py-4 text-center text-sm font-medium text-gray-500">{summary.holiday}</td>
                      <td className="px-6 py-4 text-center text-sm text-gray-600">{summary.total}</td>
                      <td className="px-6 py-4 text-center">
                        <div className="flex items-center justify-center gap-2">
                          <div className="w-16 bg-gray-200 rounded-full h-2">
                            <div className="bg-green-500 rounded-full h-2" style={{ width: `${percentage}%` }}></div>
                          </div>
                          <span className="text-sm font-medium text-gray-700">{percentage}%</span>
                        </div>
                       </td>
                     </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {!showHRView && !showManagerView && currentUser && (
        <div className="bg-white rounded-xl shadow-sm overflow-hidden">
          <div className="px-6 py-4 border-b border-gray-200">
            <h2 className="text-lg font-semibold text-gray-800">My Attendance Calendar - {months[selectedMonth]} {selectedYear}</h2>
          </div>
          <div className="p-4">
            <div className="grid grid-cols-7 gap-1">
              {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map(day => (
                <div key={day} className="text-center text-sm font-medium text-gray-600 py-2">
                  {day}
                </div>
              ))}
              {renderEmployeeCalendar(currentUser)}
            </div>
          </div>
        </div>
      )}

      {showCheckModal && selectedDate && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50">
          <div className="bg-white rounded-xl shadow-xl max-w-md w-full">
            <div className="px-6 py-4 border-b border-gray-200 flex justify-between items-center">
              <div>
                <h2 className="text-xl font-semibold text-gray-800">Mark Attendance</h2>
                <p className="text-sm text-gray-500">{selectedDate.toDateString()}</p>
              </div>
              <button onClick={() => {
                setShowCheckModal(false);
                setSelectedDate(null);
                setCheckInTime('');
                setCheckOutTime('');
              }} className="text-gray-400 hover:text-gray-600">
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            
            <div className="p-6">
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-1">Check In Time</label>
                <div className="text-lg font-semibold text-green-600">{checkInTime || 'Not checked in yet'}</div>
              </div>
              
              {checkInTime && (
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-1">Check Out Time</label>
                  <div className="text-lg font-semibold text-red-600">{checkOutTime || 'Not checked out yet'}</div>
                </div>
              )}
              
              <button
                onClick={handleCheckInOut}
                className="w-full py-3 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition font-medium"
              >
                {!checkInTime ? 'Check In' : !checkOutTime ? 'Check Out' : 'Completed'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Attendance;